import React, { useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { PlayerAvatar } from "../PlayerAvatar";
import { stColors } from "./NhlSpecialTeams";
import { NhlApiBase } from "../../lib/nhlApiBase";

const GREY = "#8A94A6";
const STEP_MS = 4500;
const KINDS = [["F1", "Line 1"], ["F2", "Line 2"], ["F3", "Line 3"], ["F4", "Line 4"], ["D1", "Pair 1"], ["D2", "Pair 2"], ["D3", "Pair 3"], ["G", "Goalies"], ["X", "Scratches"]];

// One team's sets keyed by kind: F1-F4 lines, D1-D3 pairs, both goalies, scratches.
const useSets = (gameId, team) => {
    const NB = useContext(NhlApiBase);
    const { data: lu } = useQuery({ queryKey: ["nhl-gc-lineup", gameId, team?.abbr], queryFn: () => api.get(`${NB}/lineup`, { params: { team: team.abbr, game: gameId } }).then((r) => r.data), enabled: !!team?.abbr, staleTime: 60000 });
    const { data: ro } = useQuery({ queryKey: ["nhl-gc-roster", gameId, team?.abbr], queryFn: () => api.get(`${NB}/game-roster`, { params: { team: team.abbr, game: gameId } }).then((r) => r.data), enabled: !!team?.abbr, staleTime: 60000 });
    return useMemo(() => {
        if (!["confirmed", "auto", "last_game"].includes(lu?.status)) return {};
        const L = lu?.lineup || {};
        const map = {};
        (ro?.players || []).forEach((p) => { map[p.id] = p; });
        const fill = (arr) => (arr || []).filter((s) => s && s.player_id).map((s) => ({ id: s.player_id, name: map[s.player_id]?.name || s.player_name, jersey: map[s.player_id]?.jersey, photo: map[s.player_id]?.photo_url, pos: map[s.player_id]?.position }));
        const out = {};
        (L.forward_lines || []).slice(0, 4).forEach((l, i) => { const p = fill(l); if (p.length) out[`F${i + 1}`] = p; });
        (L.d_pairs || []).slice(0, 3).forEach((l, i) => { const p = fill(l); if (p.length) out[`D${i + 1}`] = p; });
        // Only the goalie in net gets the team colour (starter until the backup comes on).
        const played = new Set((ro?.goalies || []).filter((x) => x.toi && x.toi !== "00:00").map((x) => String(x.id)));
        const g = fill(L.goalies);
        const inNet = g.length > 1 && played.has(String(g[1].id)) ? 1 : 0;
        if (g.length) out.G = g.map((p, n) => ({ ...p, plain: n !== inNet }));
        const x = fill(L.reserves);
        if (x.length) out.X = x;
        return out;
    }, [lu, ro]);
};

const Card = ({ p, c, k }) => {
    const parts = String(p.name || "").trim().split(/\s+/);
    const first = parts.length > 1 ? parts[0] : "";
    const last = parts.length > 1 ? parts.slice(1).join(" ") : parts[0] || "";
    return (
        <div className={`h-[56px] sm:h-[68px] rounded-lg border px-1 sm:px-1.5 py-1 min-w-0 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-1.5 animate-[bcPop_.5s_ease-out_both] ${p.plain ? "border-white/15 bg-white/[0.04]" : ""}`}
            style={{ animationDelay: `${120 + k * 70}ms`, ...(p.plain ? {} : { background: `linear-gradient(180deg, ${c}55, ${c}22)`, borderColor: `${c}AA` }) }} data-testid={`broadcast-player-${p.id}`}>
            <span className="sm:hidden"><PlayerAvatar photo={p.photo} name={p.name} sizePx={26} showTierRing={false} /></span>
            <span className="hidden sm:inline-flex"><PlayerAvatar photo={p.photo} name={p.name} sizePx={44} showTierRing={false} /></span>
            <span className="flex flex-col min-w-0 flex-1 gap-[1px] text-center sm:text-left w-full">
                <span className="hidden sm:block text-[9px] font-medium text-white/70 leading-[1.05] truncate">{first}</span>
                <span className="text-[9px] sm:text-[12px] font-black text-white leading-[1.05] truncate sm:line-clamp-2 sm:whitespace-normal">{last}</span>
                <span className="hidden sm:block text-[10px] font-black uppercase tracking-wider text-white/80 tabular-nums leading-none mt-[1px] truncate">{p.pos || "—"}<span className="text-white/30"> · </span>#{p.jersey ?? "—"}</span>
            </span>
        </div>
    );
};

// Broadcast lines: ONE row — [lines icon + label] | away set | home set. Both teams always show the same set.
export function BroadcastLineup({ gameId, away, home, colorBy: rawColors = {} }) {
    const colorBy = stColors(away?.abbr, home?.abbr, rawColors);
    const a = useSets(gameId, away);
    const h = useSets(gameId, home);
    const kinds = useMemo(() => KINDS.filter(([k]) => a[k] || h[k]), [a, h]);
    const [i, setI] = useState(0);
    useEffect(() => {
        if (kinds.length < 2) return undefined;
        const t = setInterval(() => setI((x) => (x + 1) % kinds.length), STEP_MS);
        return () => clearInterval(t);
    }, [kinds.length]);
    if (!kinds.length) return null;
    const [k, label] = kinds[i % kinds.length];
    const TeamLogo = ({ t }) => <img src={t?.logo_url || `https://assets.nhle.com/logos/nhl/svg/${t?.abbr}_dark.svg`} alt={t?.abbr} className="h-7 w-7 sm:h-10 sm:w-10 object-contain" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />;
    const side = (team, set) => (
        <div className="flex-1 min-w-0 flex items-center gap-1.5 sm:gap-2.5" data-testid={`broadcast-side-${team?.abbr}`}>
            <div className="shrink-0 w-10 sm:w-16 flex flex-col items-center gap-0.5">
                <TeamLogo t={team} />
                <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-wider text-white/80 text-center leading-tight whitespace-nowrap">{label}</span>
            </div>
            <div className="flex-1 min-w-0 grid gap-1 sm:gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.max(set?.length || 1, 1)}, minmax(0, 1fr))` }}>
                {(set || []).map((p, n) => <Card key={p.id} p={p} c={colorBy[team.abbr] || GREY} k={n} />)}
            </div>
        </div>
    );
    return (
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#0E0F16] px-2 sm:px-3 py-2 sm:py-2.5" data-testid="nhl-broadcast-lineup">
            <div key={k} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 animate-[bcSlide_.55s_cubic-bezier(.2,.8,.2,1)]" data-testid={`broadcast-slide-${k}`}>
                {side(away, a[k])}
                <span className="h-px sm:h-auto sm:w-px self-stretch bg-white/10 shrink-0" />
                {side(home, h[k])}
            </div>
            <style>{"@keyframes bcSlide{from{opacity:0;transform:translateX(28px)}to{opacity:1;transform:none}}@keyframes bcPop{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}"}</style>
        </div>
    );
}
